import React, { useEffect, useRef, useState } from 'react';
import {
  AE, AU, BD, BH, DE, ES, FR, GB, IN, IT, KE, KW, LK, MY, NG, NL, NP, NZ, OM, PK, QA, SA, SG, US, ZA,
} from 'country-flag-icons/react/3x2';

export const COUNTRY_CODES = [
  { code: '91', label: 'India', Flag: IN },
  { code: '1', label: 'United States / Canada', Flag: US },
  { code: '44', label: 'United Kingdom', Flag: GB },
  { code: '971', label: 'United Arab Emirates', Flag: AE },
  { code: '966', label: 'Saudi Arabia', Flag: SA },
  { code: '974', label: 'Qatar', Flag: QA },
  { code: '965', label: 'Kuwait', Flag: KW },
  { code: '968', label: 'Oman', Flag: OM },
  { code: '973', label: 'Bahrain', Flag: BH },
  { code: '61', label: 'Australia', Flag: AU },
  { code: '64', label: 'New Zealand', Flag: NZ },
  { code: '65', label: 'Singapore', Flag: SG },
  { code: '60', label: 'Malaysia', Flag: MY },
  { code: '977', label: 'Nepal', Flag: NP },
  { code: '880', label: 'Bangladesh', Flag: BD },
  { code: '94', label: 'Sri Lanka', Flag: LK },
  { code: '92', label: 'Pakistan', Flag: PK },
  { code: '27', label: 'South Africa', Flag: ZA },
  { code: '234', label: 'Nigeria', Flag: NG },
  { code: '254', label: 'Kenya', Flag: KE },
  { code: '49', label: 'Germany', Flag: DE },
  { code: '33', label: 'France', Flag: FR },
  { code: '39', label: 'Italy', Flag: IT },
  { code: '34', label: 'Spain', Flag: ES },
  { code: '31', label: 'Netherlands', Flag: NL },
];

export const EMPTY_PHONE = { countryCode: '91', number: '' };

/** "+919876543210", or '' when the number can't be valid for the chosen country. */
export function toE164Phone({ countryCode, number }) {
  const national = String(number || '').replace(/\D/g, '').replace(/^0+/, '');
  if (countryCode === '91' && !/^[6-9]\d{9}$/.test(national)) return '';
  const digits = `${countryCode}${national}`;
  if (national.length < 6 || digits.length < 11 || digits.length > 15) return '';
  return `+${digits}`;
}

/** "+919876543210" -> "+91 98765 43210"; other countries stay as sent. */
export function formatPhone(e164) {
  const value = String(e164 || '');
  return /^\+91\d{10}$/.test(value) ? `+91 ${value.slice(3, 8)} ${value.slice(8)}` : value;
}

/** Wording for where the code goes, from the backend's phoneOtpChannel. */
export const channelLabel = (channel) => (channel === 'whatsapp' ? 'on WhatsApp' : 'by SMS');

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <line x1="11" y1="18" x2="13" y2="18" />
  </svg>
);

const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/** Native <option>s can't show images (and Windows has no flag emoji), hence a custom listbox. */
function CountryPicker({ id, value, onChange, disabled, compact }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const selectedIndex = Math.max(0, COUNTRY_CODES.findIndex((c) => c.code === value));
  const selected = COUNTRY_CODES[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.focus();
    listRef.current?.children[active]?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  const openList = () => {
    setActive(selectedIndex);
    setOpen(true);
  };

  const choose = (index) => {
    onChange(COUNTRY_CODES[index].code);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const onListKeyDown = (e) => {
    if (e.key === 'ArrowDown') setActive((i) => Math.min(COUNTRY_CODES.length - 1, i + 1));
    else if (e.key === 'ArrowUp') setActive((i) => Math.max(0, i - 1));
    else if (e.key === 'Home') setActive(0);
    else if (e.key === 'End') setActive(COUNTRY_CODES.length - 1);
    else if (e.key === 'Enter' || e.key === ' ') choose(active);
    else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      if (e.key === 'Escape') buttonRef.current?.focus();
      return;
    } else if (/^[a-z]$/i.test(e.key)) {
      const next = COUNTRY_CODES.findIndex((c) => c.label.toLowerCase().startsWith(e.key.toLowerCase()));
      if (next >= 0) setActive(next);
    } else return;
    e.preventDefault();
  };

  const listId = `${id}-countries`;
  return (
    <div className="phone-country" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`phone-country-button${compact ? ' is-compact' : ''}`}
        aria-label={`Country code: ${selected.label} +${selected.code}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            openList();
          }
        }}
        disabled={disabled}
      >
        <selected.Flag className="phone-flag" title={selected.label} />
        <span>+{selected.code}</span>
        <Chevron />
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          className="phone-country-list"
          aria-label="Country code"
          aria-activedescendant={`${listId}-${active}`}
          onKeyDown={onListKeyDown}
        >
          {COUNTRY_CODES.map((c, index) => (
            <li
              key={c.code + c.label}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === selectedIndex}
              className={`phone-country-option${index === active ? ' is-active' : ''}${
                index === selectedIndex ? ' is-selected' : ''
              }`}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(index)}
            >
              <c.Flag className="phone-flag" aria-hidden />
              <span className="phone-country-name">{c.label}</span>
              <span className="phone-country-code">+{c.code}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

const cleanNumber = (raw) => raw.replace(/[^\d\s-]/g, '').slice(0, 18);

/** The server's message when it rejects the number itself (e.g. already registered). */
export function phoneErrorFrom(data) {
  if (data?.field !== 'phone') return '';
  const msg = Array.isArray(data.message) ? data.message[0] : data.message;
  return typeof msg === 'string' ? msg : '';
}

/** variant="boxed": label inside the bordered box, flag picker and number on one line. */
function PhoneField({ id = 'phone', label = 'Mobile number*', value, onChange, disabled, hint, error, variant, className = '' }) {
  const errorId = `${id}-error`;
  const errorText = error ? (
    <p className="phone-error" id={errorId} role="alert">
      {error}
    </p>
  ) : null;
  const invalidProps = error ? { 'aria-invalid': true, 'aria-describedby': errorId } : {};

  if (variant === 'boxed') {
    return (
      <>
        <div className={`fl-field fl-phone ${error ? 'is-invalid ' : ''}${className}`.trim()}>
          <label htmlFor={id}>{label}</label>
          <div className="fl-phone-row">
            <CountryPicker
              id={id}
              compact
              value={value.countryCode}
              onChange={(countryCode) => onChange({ ...value, countryCode })}
              disabled={disabled}
            />
            <input
              type="tel"
              id={id}
              name={id}
              inputMode="tel"
              autoComplete="tel-national"
              value={value.number}
              onChange={(e) => onChange({ ...value, number: cleanNumber(e.target.value) })}
              disabled={disabled}
              required
              {...invalidProps}
            />
          </div>
        </div>
        {errorText}
      </>
    );
  }
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div className="phone-field">
        <CountryPicker
          id={id}
          value={value.countryCode}
          onChange={(countryCode) => onChange({ ...value, countryCode })}
          disabled={disabled}
        />
        <div className="input-with-icon">
          <span className="input-leading-icon"><PhoneIcon /></span>
          <input
            type="tel"
            id={id}
            name={id}
            inputMode="tel"
            autoComplete="tel-national"
            value={value.number}
            onChange={(e) => onChange({ ...value, number: cleanNumber(e.target.value) })}
            placeholder="Enter your mobile number"
            disabled={disabled}
            required
            {...invalidProps}
          />
        </div>
      </div>
      {errorText}
      {hint && !error ? <p className="phone-hint">{hint}</p> : null}
    </div>
  );
}

export default PhoneField;
